import { getExpiresIn } from './utils.js';
import { BROWSER } from 'esm-env';
import { SeverityNumber } from '@opentelemetry/api-logs';
import { IS_PLAYWRIGHT_TEST_RUNNER } from '@bitskico/sveltekit-utils/env';
let z;
const getZod = async () => {
    if (!z) {
        const { z: zod } = await import('zod');
        z = zod;
    }
    return z;
};
const isEncodedJson = (validator) => 'shape' in validator || 'elements' in validator;
const encodeBase64 = (value) => btoa(JSON.stringify(value)).replace(/=+$/, '');
const decodeBase64 = (str) => JSON.parse(atob(str));
/**
 * This class is used to get and set the various cookies that the proxy uses.
 * There are 2 types of cookies:
 *
 * - App cookies: These cookies are scoped to the current app and are used for
 *   more secure state, e.g. elevated sessions with write permissions.
 * - Device cookies: These cookies are scoped to the current device and shared
 *   between all apps, and they are used for state that is less secure overall.
 *   e.g. read-only sessions, device identifiers, etc.
 *
 * Some of these settings have to be overridden for local development, and
 * distinguishing which is which and using the individual settings all over the
 * place is needlessly complicated. Thus, the abstracted methods.
 */
const COOKIE_WRITER_IMPORT = (BROWSER
    ? import('./writers/browser.js')
    : IS_PLAYWRIGHT_TEST_RUNNER
        ? import('./writers/playwright.js')
        : import('./writers/server.js')).then((m) => m.default);
export class CookieManager {
    constructor(defs, context, writer) {
        this.defs = defs;
        this.context = context;
        this.validators = {};
        if (writer) {
            this._cookies = Promise.resolve(writer);
        }
    }
    get writer() {
        if (!this._cookies) {
            this._cookies = COOKIE_WRITER_IMPORT.then((create) => {
                if (BROWSER) {
                    return create(undefined);
                }
                else {
                    if (!this.context) {
                        throw new Error(typeof process !== 'undefined' && process.env.PLAYWRIGHT
                            ? 'Cookie managers require a Playwright Browser Context when used in Playwright tests, use `withContext` to create a manager with a context'
                            : 'Cookie managers require a RequestEvent when used in SSR mode, use `withContext` to create a manager with a requestEvent');
                    }
                    return create(this.context);
                }
            });
        }
        return this._cookies;
    }
    async getValidator(name) {
        const z = await getZod();
        if (!this.validators[name]) {
            this.validators[name] = this.defs[name].validator(z);
        }
        return this.validators[name];
    }
    async getCookiePrefix(name) {
        const { prefix } = this.defs[name];
        return typeof prefix === 'function' ? await prefix(this) : prefix;
    }
    async getCookieKey(name, value, passedKey) {
        const { key = name } = this.defs[name];
        const prefix = await this.getCookiePrefix(name);
        const reifiedKey = typeof key === 'function' ? await key(this, passedKey, value) : passedKey ?? String(key);
        if (!reifiedKey) {
            return undefined;
        }
        return [prefix, reifiedKey].filter((k) => k).join('');
    }
    async getCookieParseOptions(name, options) {
        const { parse } = this.defs[name];
        return {
            decode: decodeURIComponent,
            ...(typeof parse === 'function' ? await parse(this, options) : parse),
            ...options,
        };
    }
    async getCookieSerializeOptions(name, options) {
        const { serialize, expiresIn } = this.defs[name];
        const serializeOptions = {
            path: '/',
            secure: true,
            expires: getExpiresIn(expiresIn ?? { days: 400 }),
            encode: encodeURIComponent,
            ...(typeof serialize === 'function' ? await serialize(this, options) : serialize),
            ...options,
        };
        if (BROWSER && serializeOptions.httpOnly) {
            throw new Error(`Cannot set httpOnly cookie in browser context, cookie name: ${String(name)}`);
        }
        return serializeOptions;
    }
    async deserialize(name, str) {
        const validator = await this.getValidator(name);
        try {
            const decoded = isEncodedJson(validator) ? decodeBase64(str) : str;
            return validator.parse(decoded);
        }
        catch (e) {
            if (this.context && 'locals' in this.context) {
                this.context.locals?.telemetry?.logger.emit({
                    severityNumber: SeverityNumber.WARN,
                    body: `Failed to parse cookie: ${String(name)} with value ${str}, error: ${e}`,
                });
            }
        }
    }
    async serialize(name, value) {
        const validator = await this.getValidator(name);
        return isEncodedJson(validator) ? encodeBase64(value) : value;
    }
    async get(name, options) {
        const key = await this.getCookieKey(name, undefined, options?.key);
        if (!key)
            return;
        const definition = this.defs[name];
        const parseOptions = await this.getCookieParseOptions(name, options?.parse);
        const writer = await this.writer;
        let cookie = await writer.get(key, { doNotParse: true });
        if (!cookie && definition.fallbackValue) {
            cookie =
                typeof definition.fallbackValue === 'function'
                    ? await definition.fallbackValue(this)
                    : definition.fallbackValue;
        }
        if (!cookie)
            return;
        const decoded = parseOptions.decode(cookie);
        return await this.deserialize(name, decoded);
    }
    async getAll(name, options) {
        const prefix = await this.getCookiePrefix(name);
        if (!prefix) {
            throw new Error(`Cannot get all cookies for ${String(name)}, no prefix defined`);
        }
        const parseOptions = await this.getCookieParseOptions(name, options?.parse);
        const writer = await this.writer;
        const cookies = await writer.getAll({ doNotParse: true });
        const result = {};
        for (const [cookieKey, value] of Object.entries(cookies)) {
            if (cookieKey.startsWith(prefix)) {
                const decoded = parseOptions.decode(value);
                const cookie = await this.deserialize(name, decoded);
                if (cookie) {
                    result[cookieKey] = cookie;
                }
            }
        }
        return result;
    }
    async set(name, value, options) {
        const key = await this.getCookieKey(name, value, options?.key);
        if (!key) {
            throw new Error(`Cannot set cookie ${String(name)}, no key defined`);
        }
        const serializeOptions = await this.getCookieSerializeOptions(name, options?.serialize);
        const serialized = await this.serialize(name, value);
        const encodedValue = serializeOptions.encode(serialized);
        const writer = await this.writer;
        await writer.set(key, encodedValue, serializeOptions);
    }
    async delete(name, options) {
        const key = await this.getCookieKey(name, undefined, options?.key);
        if (!key) {
            throw new Error(`Cannot set cookie ${String(name)}, no key defined`);
        }
        const serializeOptions = await this.getCookieSerializeOptions(name, options?.serialize);
        const writer = await this.writer;
        await writer.remove(key, { ...serializeOptions, maxAge: 0, expires: undefined });
    }
    async deleteAll(name, options) {
        const prefix = await this.getCookiePrefix(name);
        if (!prefix) {
            throw new Error(`Cannot get all cookies for ${String(name)}, no prefix defined`);
        }
        const writer = await this.writer;
        const cookies = writer.getAll();
        const serializeOptions = await this.getCookieSerializeOptions(name, options?.serialize);
        for (const cookieKey of Object.keys(cookies)) {
            if (cookieKey.startsWith(prefix)) {
                await writer.remove(cookieKey, { ...serializeOptions, maxAge: 0, expires: undefined });
            }
        }
    }
    async getPageContext() {
        return (await this.writer).getPageContext();
    }
    extend(extra) {
        return new CookieManager({
            ...this.defs,
            ...extra,
        });
    }
    withContext(context) {
        if (!IS_PLAYWRIGHT_TEST_RUNNER) {
            throw new Error('You can only use withContext in a Playwright environment, ensure the PLAYWRIGHT or VITE_PLAYWRIGHT environment variable is set and you are using the cookie manager in a Playwright context');
        }
        return new CookieManager(this.defs, context);
    }
    withEvent(event) {
        if (BROWSER) {
            throw new Error('You can only use withEvent in SSR mode. If using the CookieManager in the browser you do not need to use withEvent, you can use the cookie manager directly instead.');
        }
        else if (IS_PLAYWRIGHT_TEST_RUNNER) {
            throw new Error('You cannot use withEvent in a Playwright context, use withContext instead and pass a Playwright BrowserContext to the manager');
        }
        return new CookieManager(this.defs, event);
    }
    withWriter(writer) {
        return new CookieManager(this.defs, undefined, writer);
    }
}
